import { Button, IconButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Card } from 'src/components/UI/Card';
import TitleTooltip from 'src/components/UI/TitleTooltip';

interface IBGTopicsBarProps {
  topics: string[];
  topicCount: number;
  onManageTopics?: () => void;
  siteSoftDeleted: boolean;
}

/**
 * `BGTopicsBar` renders a horizontal bar showing the current topic count,
 * topic label chips, and a "Manage Topics" action button.
 *
 * @component
 */
function BGTopicsBar({
  topics,
  topicCount,
  onManageTopics,
  siteSoftDeleted
}: IBGTopicsBarProps) {
  return (
    <Card className="mt-6 !p-0">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left: Topics count + labels */}
        <div className="flex items-center gap-2.5">
          {/* Topic count */}
          <div className="flex flex-col justify-center min-w-[70px]">
            <div className="flex items-center gap-1.5">
              <TitleTooltip>
                <TitleTooltip.Text
                  title="Topics"
                  className="!text-[#5D5F6C]"
                >
                  <TitleTooltip.Tooltip
                    tooltip={
                      <p className="text-primary text-sm font-medium">
                        The number of topics configured for blog post
                        generation.
                      </p>
                    }
                  />
                </TitleTooltip.Text>
              </TitleTooltip>
            </div>
            <div className="text-primary text-2xl md:text-3xl font-bold">
              {topicCount}
            </div>
          </div>

          {/* Topic labels */}
          <div className="flex items-center gap-3 flex-wrap">
            {topics.map((topic, index) => (
              <span
                key={index}
                className="capitalize text-sm font-semibold px-2.5 py-1 rounded-2xl bg-[#ECFDF3] text-[#1A927B] whitespace-nowrap"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Navigation + Manage Topics button */}
        <div className="flex items-center gap-4">
          <Button
            onClick={onManageTopics}
            disabled={siteSoftDeleted}
            variant="contained"
            sx={{
              backgroundColor: 'var(--color-primary, #120242)',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '6px',
              fontSize: '14px',
              py: 1,
              px: 2.5,
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: 'rgba(18, 2, 66, 0.9)'
              }
            }}
          >
            Manage Topics
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default BGTopicsBar;
